import { createContext, Dispatch, useContext, useReducer } from "react";

import { ValuationManagerContextState } from "./types/context";
import { Navigation } from "./types/navigation";

const initialState: ValuationManagerContextState = {
  escalation: {},
  pageName: Navigation.Qualifications,
};

export type ActionType = "UpdateValuationManagerContext" | "Reset";

export type ActionData<T extends ActionType> =
  T extends "UpdateValuationManagerContext"
    ? ValuationManagerContextState
    : T extends "Reset"
      ? unknown
      : never;

export type UpdateStateFn = <T extends ActionType>(
  type: T,
  data: ActionData<T>,
) => void;

export type Action<T extends ActionType> = {
  type: T;
  data: Partial<ActionData<T>>;
};

type ReduceAction = Action<"UpdateValuationManagerContext"> | Action<"Reset">;

const ValuationManagerContext = createContext(initialState);

const ValuationManagerDispatchContext = createContext<Dispatch<ReduceAction> | null>(
  null,
);

interface ValuationManagerProviderProps {
  children: React.ReactNode;
}

export const ValuationManagerProvider: React.FC<ValuationManagerProviderProps> = ({
  children,
}: ValuationManagerProviderProps) => {
  const [valuationManagerState, dispatch] = useReducer(
    valuationManagerReducer,
    initialState,
  );

  return (
    <ValuationManagerContext.Provider value={valuationManagerState}>
      <ValuationManagerDispatchContext.Provider value={dispatch}>
        {children}
      </ValuationManagerDispatchContext.Provider>
    </ValuationManagerContext.Provider>
  );
};

export function useValuationManagerContext() {
  return useContext(ValuationManagerContext);
}

export function useValuationManagerDispatch() {
  return useContext(ValuationManagerDispatchContext);
}

function valuationManagerReducer(
  state: ValuationManagerContextState,
  action: ReduceAction,
) {
  switch (action.type) {
    case "UpdateValuationManagerContext": {
      return {
        ...state,
        ...action.data,
      };
    }
    case "Reset": {
      return { ...initialState };
    }
    default: {
      throw Error("Unknown action");
    }
  }
}
